import { products as allProducts } from '../data/geminiprod.js';

// Create a lookup map for efficient product retrieval using product name as the key.
const productMap = new Map(allProducts.map(p => [p.name, p]));

/**
 * Enriches a list of routine products with full details from the master product list.
 * @param {Array} routineProducts - An array of product objects from the user's routine.
 * @returns {Array} An array of product objects, enriched with full details.
 */
const enrichProducts = (routineProducts) => {
  if (!routineProducts || !Array.isArray(routineProducts)) {
    return [];
  }
  return routineProducts.map(routineProduct => {
    // Find the full product details from the map.
    const fullProduct = productMap.get(routineProduct.name);
    // Merge the full details with the routine-specific data (like 'step').
    // Routine-specific data will override master data if there are conflicts.
    return fullProduct ? { ...fullProduct, ...routineProduct } : routineProduct;
  });
};

/**
 * Processes a user's routine, enriches it with full product details,
 * and formats it into a 7-day schedule.
 * @param {Object} routine - The user's routine object.
 * @returns {Object} The processed and enriched 7-day routine.
 */
export const processRoutineForDisplay = (routine) => {
  if (!routine || typeof routine !== 'object' || Object.keys(routine).length === 0) {
    return {};
  }

  // If the routine is already in the 7-day format, enrich each day's products.
  if (routine.Monday) {
    const enrichedRoutine = {};
    Object.keys(routine).forEach(day => {
      enrichedRoutine[day] = {
        AM: enrichProducts(routine[day].AM),
        PM: enrichProducts(routine[day].PM),
        Weekly: enrichProducts(routine[day].Weekly),
      };
    });
    return enrichedRoutine;
  }

  // Check if it's the simple AM/PM/Weekly format for conversion.
  const isSimpleFormat = routine.AM || routine.PM || routine.Weekly;
  if (!isSimpleFormat) {
    return routine; // Return unrecognized format as is.
  }

  const newRoutine = {};
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Enrich the basic AM/PM routines.
  const enrichedAM = enrichProducts(routine.AM);
  const enrichedPM = enrichProducts(routine.PM);

  // Assign the enriched AM/PM routines to every day of the week.
  daysOfWeek.forEach(day => {
    newRoutine[day] = {
      AM: enrichedAM,
      PM: enrichedPM,
    };
  });

  // Distribute and enrich weekly products.
  const weeklyDistributionDays = ['Saturday', 'Sunday', 'Friday', 'Wednesday', 'Monday', 'Thursday', 'Tuesday'];
  const weeklyProducts = routine.Weekly || [];

  weeklyProducts.forEach((product, index) => {
    const dayForProduct = weeklyDistributionDays[index % weeklyDistributionDays.length];
    
    if (!newRoutine[dayForProduct].Weekly) {
      newRoutine[dayForProduct].Weekly = [];
    }
    
    const fullProduct = productMap.get(product.name);
    // Create the final product object with all details and the correct step number.
    const productWithStep = { 
        ...fullProduct, 
        ...product, 
        step: newRoutine[dayForProduct].Weekly.length + 1 
    };
    newRoutine[dayForProduct].Weekly.push(productWithStep);
  });

  return newRoutine;
};
